/**
 * Customer Drilldown Drawer
 *
 * Slide-out panel showing customers with predictions and their detailed forecasts.
 * Design choice: Only shows customers with predictions in the selected period (Option B).
 */

import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@/components/ui/sheet'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'
import { Users, Package, TrendingUp, TrendingDown, ChevronRight, ArrowLeft } from 'lucide-react'
import {
  getCustomersWithPredictions,
  getCustomerForecast
} from '../../services/forecastService'

// Product colours for consistency
const PRODUCT_COLOURS = {
  "Lion's Mane": '#F54A00',
  'Grey Oyster': '#009689',
  'Coral Tooth': '#6366F1',
  'Pink Oyster': '#EC4899',
  'Yellow Oyster': '#F59E0B',
  'King Oyster': '#8B5CF6',
  'Shiitake': '#10B981',
  'Chestnut': '#78716C'
}

const getProductColour = (product) => {
  return PRODUCT_COLOURS[product] || '#94a3b8'
}

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
      }}>
        <p className="text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}</span>
            </div>
            <span className="font-medium">{entry.value.toFixed(1)}kg</span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

// Customer list item component
function CustomerListItem({ customer, onClick }) {
  return (
    <button
      onClick={() => onClick(customer)}
      className="w-full text-left p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{customer.name}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-xs">
              {customer.type}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {customer.deliveryCount} deliveries
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="font-semibold">{customer.totalVolume.toFixed(1)}kg</p>
            <p className="text-xs text-muted-foreground">
              {customer.productCount} products
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </button>
  )
}

// Customer detail view component
function CustomerDetailView({ customer, period, offset, onBack }) {
  const [forecast, setForecast] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadForecast = async () => {
      try {
        setLoading(true)
        const data = await getCustomerForecast(customer.id, period, offset)
        setForecast(data)
      } catch (error) {
        console.error('Error loading customer forecast:', error)
      } finally {
        setLoading(false)
      }
    }

    loadForecast()
  }, [customer.id, period, offset])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading customer forecast...</p>
      </div>
    )
  }

  const volumeChangePositive = forecast?.summary.volumeChangePercent >= 0

  return (
    <div className="space-y-4">
      {/* Back button and header */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to list
      </button>

      <div>
        <h3 className="text-lg font-semibold">{customer.name}</h3>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline">{customer.type}</Badge>
          <span className="text-sm text-muted-foreground">{forecast?.period}</span>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">Predicted Volume</p>
            <p className="text-xl font-bold">{forecast?.summary.predictedVolume.toFixed(1)}kg</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground">vs Historical</p>
            <div className="flex items-center gap-1">
              {volumeChangePositive ? (
                <TrendingUp className="h-4 w-4 text-[#10B981]" />
              ) : (
                <TrendingDown className="h-4 w-4 text-[#EF4444]" />
              )}
              <span className={`text-xl font-bold ${volumeChangePositive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {volumeChangePositive ? '+' : ''}{forecast?.summary.volumeChangePercent.toFixed(0)}%
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Product breakdown chart */}
      {forecast?.byProduct.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-2">By Product</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={forecast.byProduct}
                layout="vertical"
                margin={{ top: 5, right: 10, left: 60, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
                />
                <YAxis
                  type="category"
                  dataKey="strain"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  width={55}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="predicted"
                  name="Predicted"
                  radius={[0, 4, 4, 0]}
                >
                  {forecast.byProduct.map((entry, index) => (
                    <Cell key={index} fill={getProductColour(entry.strain)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Prediction list */}
      <div>
        <p className="text-sm font-medium mb-2">Upcoming Deliveries</p>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {forecast?.predictions.map((pred) => (
            <div
              key={pred.id}
              className="flex items-center justify-between p-2 rounded border border-border text-sm"
            >
              <div>
                <p className="font-medium">{pred.product?.strain}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(pred.suggested_date).toLocaleDateString('en-GB', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short'
                  })}
                </p>
              </div>
              <div className="text-right">
                <p className="font-medium">{pred.suggested_quantity.toFixed(1)}kg</p>
                <p className="text-xs text-muted-foreground">
                  {(pred.confidence_score * 100).toFixed(0)}% confidence
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function CustomerDrilldownDrawer({
  open,
  onOpenChange,
  period,
  offset = 0
}) {
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCustomer, setSelectedCustomer] = useState(null)

  useEffect(() => {
    if (open) {
      loadCustomers()
    }
  }, [open, period, offset])

  const loadCustomers = async () => {
    try {
      setLoading(true)
      setSelectedCustomer(null)
      const data = await getCustomersWithPredictions(period, offset)
      setCustomers(data)
    } catch (error) {
      console.error('Error loading customers:', error)
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer)
  }

  const handleBack = () => {
    setSelectedCustomer(null)
  }

  // Calculate totals
  const totalVolume = customers.reduce((sum, c) => sum + c.totalVolume, 0)
  const totalDeliveries = customers.reduce((sum, c) => sum + c.deliveryCount, 0)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Customer Forecast
          </SheetTitle>
          <SheetDescription>
            {selectedCustomer
              ? `Detailed forecast for ${selectedCustomer.name}`
              : `${customers.length} customers with predictions`
            }
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <p className="text-muted-foreground">Loading customers...</p>
            </div>
          ) : selectedCustomer ? (
            <CustomerDetailView
              customer={selectedCustomer}
              period={period}
              offset={offset}
              onBack={handleBack}
            />
          ) : (
            <div className="space-y-4">
              {/* Summary stats */}
              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Customers</span>
                    </div>
                    <p className="text-2xl font-bold mt-1">{customers.length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Total Volume</span>
                    </div>
                    <p className="text-2xl font-bold mt-1">{totalVolume.toFixed(0)}kg</p>
                  </CardContent>
                </Card>
              </div>

              {/* Customer list */}
              <div className="space-y-2">
                {customers.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No customers with predictions in this period
                  </p>
                ) : (
                  customers.map((customer) => (
                    <CustomerListItem
                      key={customer.id}
                      customer={customer}
                      onClick={handleSelectCustomer}
                    />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
